/**
 * Pure helpers for the Launch Readiness page: goal filtering, done/total
 * counting, and default-tab selection.
 *
 * No React imports — jest-tested directly (utils/__tests__).
 */

import { MILESTONE_CHECK_LABELS, PAYMENTS_MILESTONE_ID } from '@modules/launch-readiness/types/launchReadiness.types';

import type {
  LaunchGoalSet,
  LaunchMilestone,
} from '@modules/launch-readiness/types/launchReadiness.types';

/** Canonical tab order (mirrors backend LAUNCH_GOAL_SETS). */
export const GOAL_ORDER: LaunchGoalSet[] = ['first_value', 'monetized', 'ads_ready', 'full_production'];

export const GOAL_LABELS: Record<LaunchGoalSet, string> = {
  first_value: 'First value',
  monetized: 'Monetized',
  ads_ready: 'Ads-ready',
  full_production: 'Full production',
};

/** Milestones that belong to a goal tab, preserving backend order. */
export function filterMilestonesByGoal(
  milestones: LaunchMilestone[],
  goal: LaunchGoalSet,
): LaunchMilestone[] {
  return milestones.filter((milestone) => milestone.goalSets.includes(goal));
}

export interface GoalProgress {
  done: number;
  total: number;
}

/**
 * The `done/total` chip counts for a goal tab.
 *
 * `human_decision` milestones are checklist items, never failures (J2 spec):
 * they are excluded from BOTH numerator and denominator so an open owner
 * decision cannot read as an unmet requirement. `not_evaluated` milestones
 * (D-LR-5 — a skipped preflight row) are excluded the same way: a row that
 * was never checked must not read as an unmet requirement either. `missing`
 * and `check_unavailable` count toward the denominator only.
 */
export function countGoalProgress(
  milestones: LaunchMilestone[],
  goal: LaunchGoalSet,
): GoalProgress {
  const scoped = filterMilestonesByGoal(milestones, goal).filter(
    (milestone) => milestone.status !== 'human_decision' && milestone.status !== 'not_evaluated',
  );
  return {
    done: scoped.filter((milestone) => milestone.status === 'done').length,
    total: scoped.length,
  };
}

/**
 * Default selected tab: the first goal (in GOAL_ORDER) that still has
 * `missing` milestones; falls back to the first goal when everything is
 * missing-free.
 */
export function defaultGoalTab(milestones: LaunchMilestone[]): LaunchGoalSet {
  for (const goal of GOAL_ORDER) {
    const hasMissing = filterMilestonesByGoal(milestones, goal)
      .some((milestone) => milestone.status === 'missing');
    if (hasMissing) return goal;
  }
  return GOAL_ORDER[0];
}

/**
 * Splits a milestone detail around an emphasized warning sentence so the
 * page can render the warning in `warning.main` (the opt-in trap). When the
 * warning is not found the whole detail becomes the emphasized part.
 */
export function splitDetailAroundWarning(
  detail: string,
  warning: string,
): { before: string; warning: string; after: string } {
  const at = detail.indexOf(warning);
  if (at < 0) {
    return { before: '', warning: detail, after: '' };
  }
  return {
    before: detail.slice(0, at),
    warning,
    after: detail.slice(at + warning.length),
  };
}


export type MilestoneCheckState = 'passed' | 'failed' | 'skipped';

export interface MilestoneCheck {
  checkId: string;
  label: string;
  state: MilestoneCheckState;
}

/** Display-only projection; never changes the backend verdict or raw parity sources. */
export function milestoneCheckLine(milestone: LaunchMilestone): MilestoneCheck[] {
  if (milestone.id !== PAYMENTS_MILESTONE_ID || !milestone.sources) return [];
  return milestone.sources
    .filter((source) => source.deprecated !== true)
    .map((source) => ({
      checkId: source.checkId,
      label: MILESTONE_CHECK_LABELS[source.checkId] ?? source.label ?? source.checkId,
      state: source.status === 'passed' ? 'passed' : source.status === 'skipped' ? 'skipped' : 'failed',
    }));
}
